import { Link } from 'react-router-dom';
import type { Client, Project, ProjectUpdate, UserRole } from '../../types/crm';
import { MANAGEMENT_PROJECT_UPDATE_LIMIT, canPostManagementReply } from '../../lib/roleAccess';
import {
  formatProjectUpdateTime,
  isSystemProjectUpdate,
  latestUpdateForProject,
  latestUpdatePreview,
  projectStatusModifier,
  recentUpdatesForProject,
} from '../../utils/projectHelpers';
import { accountTypeBadgeStyle, accountTypeShortLabel } from '../../utils/crmHelpers';
import { projectDetailPath } from '../../utils/dashboardHelpers';
import { ProjectListReplyBox } from './ProjectListReplyBox';

interface ProjectListRowProps {
  project: Project;
  client?: Client;
  ownerName: string;
  updates: ProjectUpdate[];
  viewRole: UserRole;
  onReplyPosted?: () => void;
}

export function ProjectListRow({
  project,
  client,
  ownerName,
  updates,
  viewRole,
  onReplyPosted,
}: ProjectListRowProps) {
  const ref = project.reference_no ?? '—';
  const href = projectDetailPath(project);

  const isManagementList = viewRole === 'management';
  const canReply = isManagementList && canPostManagementReply(viewRole) && Boolean(onReplyPosted);
  const previewUpdates = isManagementList
    ? recentUpdatesForProject(updates, project.id, MANAGEMENT_PROJECT_UPDATE_LIMIT)
    : [];
  const latest = latestUpdateForProject(updates, project.id);

  if (isManagementList) {
    return (
      <div className="project-list-row project-list-row--management">
        <Link to={href} className="project-list-row__cell project-list-row__cell--status">
          <span className={`project-list-row__status project-status ${projectStatusModifier(project.status)}`}>
            {project.status ?? 'Ongoing'}
          </span>
        </Link>

        <Link to={href} className="project-list-row__cell project-list-row__cell--main">
          <span className="project-list-row__title">{project.title}</span>
          <span className="project-list-row__ref">{ref}</span>
        </Link>

        <Link to={href} className="project-list-row__cell project-list-row__cell--account">
          {client ? (
            <>
              {client.client_type && (
                <span
                  className="project-list-row__type"
                  style={accountTypeBadgeStyle(client.client_type)}
                >
                  {accountTypeShortLabel(client.client_type)}
                </span>
              )}
              <span className="project-list-row__account-name">{client.company_name}</span>
              <span className="project-list-row__account-code">{client.code}</span>
            </>
          ) : (
            <span className="project-list-row__account-code">No account</span>
          )}
        </Link>

        <div className="project-list-row__cell project-list-row__cell--latest">
          {previewUpdates.length > 0 ? (
            <ul className="project-list-row__updates-stack">
              {previewUpdates.map((update) => (
                <li key={update.id} className="project-list-row__update-item">
                  <span className="project-list-row__latest-time">
                    {formatProjectUpdateTime(update.created_at)}
                    {isSystemProjectUpdate(update.content) ? ' · System' : ''}
                  </span>
                  <span className="project-list-row__latest-text">
                    {latestUpdatePreview(update, 100)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <span className="project-list-row__latest-text">No updates yet.</span>
          )}
          {canReply && (
            <ProjectListReplyBox
              projectId={project.id}
              onPosted={onReplyPosted!}
            />
          )}
        </div>

        <Link to={href} className="project-list-row__cell project-list-row__cell--owner">
          <span className="project-list-row__owner">{ownerName}</span>
        </Link>

        <Link to={href} className="project-list-row__cell project-list-row__cell--chevron" aria-label="Open project">
          <span className="project-list-row__chevron" aria-hidden>
            ›
          </span>
        </Link>
      </div>
    );
  }

  return (
    <Link to={href} className="project-list-row">
      <span className={`project-list-row__status project-status ${projectStatusModifier(project.status)}`}>
        {project.status ?? 'Ongoing'}
      </span>

      <div className="project-list-row__main">
        <span className="project-list-row__title">{project.title}</span>
        <span className="project-list-row__ref">{ref}</span>
      </div>

      <div className="project-list-row__account">
        {client ? (
          <>
            {client.client_type && (
              <span
                className="project-list-row__type"
                style={accountTypeBadgeStyle(client.client_type)}
              >
                {accountTypeShortLabel(client.client_type)}
              </span>
            )}
            <span className="project-list-row__account-name">{client.company_name}</span>
            <span className="project-list-row__account-code">{client.code}</span>
          </>
        ) : (
          <span className="project-list-row__account-code">No account</span>
        )}
      </div>

      <div className="project-list-row__latest">
        {latest && (
          <span className="project-list-row__latest-time">
            {formatProjectUpdateTime(latest.created_at)}
          </span>
        )}
        <span className="project-list-row__latest-text">{latestUpdatePreview(latest)}</span>
      </div>

      <span className="project-list-row__owner">{ownerName}</span>
      <span className="project-list-row__chevron" aria-hidden>
        ›
      </span>
    </Link>
  );
}
